import { Col, Row } from './Grid';
import { FloatingLabel } from "./FloatingLabel";
import PropTypes from 'prop-types';
import { DefaultButton } from './DefaultButton';
import Label from './Label';
import { HiddenValidationControl } from './HiddenValidationControl';
import { FormControl } from 'react-bootstrap';
import { safeEval } from "../utils/safeEval";
import { useCallback, useEffect, useRef, useState } from 'react';
import { readField } from '../utils/objectUtilities';
import { validateOptions } from '../utils/generalUtils';

export const customCtrlDef = {
    toString: ({ value, args, defaultValue, def }) => {
        if (value === undefined || value === null) {
            return defaultValue;
        }
        const defKey = def.key ?? 'text';
        const defValue = def.value ?? 'value';
        let values = def.values;
        if (typeof values === 'string') {
            values = safeEval(values)(value, args);
        }
        return values?.find(v => v[defValue] == value)?.[defKey] ?? value;
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                [
                    { type: "table", field: "values", sort: [], columns: [
                        {title: t('label'), type: "text", field: "text", editable: true},
                        { title: t('value'), type: "text", field: "value", editable: true }
                    ]}
                ],
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, value, href, ...props }, { args }) => {
        const defKey = def.key ?? 'text';
        const defValue = def.value ?? 'value';
        let values = def.values;
        if (typeof values === 'string') {
            values = safeEval(values)(value, args);
        }
        validateOptions(values, defKey, defValue);
        return <TagManager tags={value} values={values} labelField={defKey} valueField={defValue} href={href ?? def.href} {...props}></TagManager>
    }
}

/**
 * A multi-select option control used for things like annotating objects with tags.
 * @component
 */
const TagManager = ({ id, tags: inputTags, values, labelField, valueField, selectableField, editableField, placeholder, required = false, hideLabel = false, disabled = false, readonly = false, onChange, onAdd, onRemove, style, href}) => {
    const [tags, setTags] = useState(inputTags);
    const validationRef = useRef();

    useEffect(() => {
        setTags(inputTags);
    }, [inputTags]);
    
    if (onChange && (onAdd || onRemove)) {
        throw new Error("TagManager requires you use either onChange or onAdd and onRemove - not both!");
    }

    const getValue = (val) => {
        if (!labelField || !valueField) {
            return val;
        }
        // needed to handle messy type conversions (string vs integer)
        // eslint-disable-next-line eqeqeq
        return values?.find(v => (valueField ? v[valueField] : v) == val);
    }

    const getDisplayValue = (val) => {
        const value = getValue(val);
        if (value) {
            return labelField ? readField(value, labelField) : value;
        }
        return '';
    }

    const add = tag => setTags(tags => {
        const newTags = [...(tags ?? []), tag];
        if (!required || newTags.length > 0) {
            if (onChange) {
                onChange(newTags);
            } else {
                onAdd(tag);
                inputTags.filter(it => newTags.every(nt => (valueField ? it[valueField] : it) !== (valueField ? nt[valueField] : nt))).forEach(tag => onRemove(tag));
            }
        }
        return newTags;
    });

    const remove = tag => setTags(tags => {
        const newTags = (tags ?? []).filter(t => t !== tag);
        if (!required || newTags.length > 0) {
            if (onChange) {
                onChange(newTags);
            } else {
                onRemove(tag);
            }
        }
        return newTags;
    });

    const editable = useCallback(tag => values?.find(v => tag == (valueField ? v[valueField] : v))?.editable ?? true, [valueField, values])

    const DisplayTag = ({tag}) => <> 
        { tag && <div className="d-inline-block tag my-1" >
            {getDisplayValue(tag)} 
            { !disabled && editable(tag) && <DefaultButton kind="delete" variant="outline-dark" className="px-1 py-0 mx-1" hideLabel={true} size="sm" onClick={() => remove(tag)}></DefaultButton> }
        </div> } 
    </>
    
    // needed to handle messy type conversions (string vs integer)
    // eslint-disable-next-line eqeqeq
    let options = values?.filter(v => (!tags || (tags?.every?.(t => t != (valueField ? v[valueField] : v)) ?? true)) && (!selectableField || v[selectableField]) && (!editableField || v[editableField]))?.map(v => {
        const val = valueField ? v[valueField] : v;
        return <option key={'opt_' + val} value={val}>{labelField ? v[labelField] : v}</option>
    })
    return readonly ? <Label id={id} hideLabel={hideLabel} placeholder={placeholder} text={tags?.length > 0 ? tags?.map(t => getDisplayValue(t)).join(', ') : ''} href={href}></Label> :
        <FloatingLabel id={id} hideLabel={hideLabel} placeholder={placeholder} required={required} value={true} href={href}>
            <Row>
                <Col className="tags"> { tags?.map(tag => 
                    <DisplayTag key={tag} tag={tag}></DisplayTag> 
                )} </Col> 
                { disabled ? null : <Col xs="auto" style={style}>
                    <FormControl as="select" onChange={e => add(e.target.value)}>
                        <option style={{display:'none'}} value="">{placeholder}</option>
                        {options}
                    </FormControl>
                </Col> }
            </Row>
            <HiddenValidationControl ref={validationRef} value={tags} required={required} ></HiddenValidationControl>
        </FloatingLabel>
};

TagManager.propTypes = {
    /**
     * An array of values representing the tags which have been selected
     */
    tags: PropTypes.arrayOf(PropTypes.any),

    /**
     * An array of objects representing the set of values that could be selected
     */
    values: PropTypes.arrayOf(PropTypes.any),

    /**
     * The field to read from the tags as the label to display
     */
    labelField: PropTypes.string,

    /**
     * The field to read from the tags as the value to put be in the values array
     */
    valueField: PropTypes.string,

    /**
     * The field to read from the tags for selectable check
     */
    selectableField: PropTypes.string,
    
    /**
    * The field to read from the tags for editable check
    */
    editableField: PropTypes.string,
    
    /**
     * Function to call when the list of selection values has been changed.
     * This is mutally exclusive with use of onAdd and onRemove
     */
    onChange: PropTypes.func,

    /**
     * Function to call with a tag is added to the list of selected tags.
     * The function should accept one parameter - value of the tag being added.
     */
    onAdd: PropTypes.func,

    /**
     * Function to call with a tag when it is removed from the list of selected tags.
     * The function should accept one parameter - value of the tag being removed.
     */
    onRemove: PropTypes.func,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string.isRequired,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    readonly: PropTypes.bool,

    /** 
     * Force the input to be disabled
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default TagManager;